import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SEARCH_PARAMS } from 'constants/dataSets';
import {
  RELATE_TO, RELATE_TO_MA, SEARCH, SORT, STATUS,
} from 'constants/formFields';
import { DEFAULT_LIMIT, DEFAULT_OFFSET, DEFAULT_ORDER_BY } from 'constants/general';
import { useGetMaterialityAssessmentsQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useGetTasksQuery, useLazyExportTasksQuery } from 'store/slices/tasksManagement/apis/tasksManagementApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { useGetValueChainMapListQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import getAccessTaskManagement from 'utils/getAccessTaskManagement';

import useDataTaskListActions from './hooks/useDataTaskListActions';
import useTaskListForm from './hooks/useTaskListForm';
import TaskManagementView from './TaskManagementView';

import type IUser from 'store/slices/user/interfaces/IUser';

function TaskManagement() {
  const [activeModal, setActiveModal] = useState(false);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const searchParam = searchParams.get(SEARCH_PARAMS.search) || '';
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);
  const { data: userData, isLoading: isUserLoading } = useUserMeQuery();

  const {
    control, getValues, setValue, watch,
  } = useTaskListForm();

  const {
    onSearchChange,
    onSortChange,
  } = useDataTaskListActions({
    setOrderBy,
    getValues,
    setValue,
  });

  const {
    data: valueChainMapList, isLoading: isLoadingVCList,
  } = useGetValueChainMapListQuery({
    limit: DEFAULT_LIMIT,
    offset: 0,
  });

  const { data: materialityAssessmentList, isFetching: isMaterialityListLoading } = useGetMaterialityAssessmentsQuery({
    orderBy: DEFAULT_ORDER_BY,
    offset,
  });

  const relatedToValues = (relatedToArray?: { id: number, name: string }[]) => {
    if (relatedToArray && relatedToArray.length > 0) {
      return relatedToArray.map((item) => item.id);
    }
    return [];
  };

  const relatedToValuesMA = (relatedToArray?: { id: number, name: string }[]) => {
    if (relatedToArray && relatedToArray.length > 0) {
      return relatedToArray.map((item) => item.id);
    }
    return [];
  };
  const { data, isFetching: isTaskListLoading } = useGetTasksQuery({
    orderBy,
    keyword: searchParam,
    offset,
    statuses: watch(STATUS)?.filter((item): item is string => item !== undefined),
    valueChainMapIds: relatedToValues(watch(RELATE_TO)),
    materialityAssessmentIds: relatedToValuesMA(watch(RELATE_TO_MA)),
  });

  const [exportTasks] = useLazyExportTasksQuery();

  const loading = isTaskListLoading || isLoadingVCList || isUserLoading || isMaterialityListLoading;
  const filterOff = watch(STATUS)?.length === 0
  && watch(RELATE_TO)?.length === 0
  && watch(SEARCH)?.length === 0
  && watch(SORT)?.orderKey === ''
  && location.search === '';

  const isEmptyList = (!data?.data?.meta?.count || data?.data?.items?.length === 0)
    && filterOff && !isTaskListLoading;
  const { meta, items } = data?.data || {};

  const onOpenModal = () => {
    setActiveModal(true);
  };

  const onCloseModal = () => {
    setActiveModal(false);
  };

  const onReadMoreClients = () => {
    setSearchParams((prev) => ({
      ...prev,
      [SEARCH_PARAMS.offset]: (offset + DEFAULT_OFFSET).toString(),
    }));
  };

  const onDownloadFile = () => {
    exportTasks({
      orderBy,
      keyword: searchParam,
      offset,
      statuses: watch(STATUS)?.filter((item): item is string => item !== undefined),
      valueChainMapIds: relatedToValues(watch(RELATE_TO)),
    });
  };

  return (
    <TaskManagementView
      isEmptyList={isEmptyList}
      onOpenModal={onOpenModal}
      onCloseModal={onCloseModal}
      activeModal={activeModal}
      control={control}
      onSearchChange={onSearchChange}
      taskList={items || []}
      loading={loading}
      onReadMoreClients={onReadMoreClients}
      hasNext={!!meta?.hasNext}
      onSortChange={onSortChange}
      valueChainMapList={valueChainMapList?.data?.items || []}
      materialityAssessmentList={materialityAssessmentList?.data?.items || []}
      userData={getAccessTaskManagement(userData?.data as IUser)}
      onDownloadFile={onDownloadFile}
    />
  );
}

export default TaskManagement;
